/**
 * 皮影贴图管理：解析文件名、加载图片、计算仿射矩阵。
 *
 * 文件名格式：{顺序}-{图内点序号}_{x}_{y}-{图内点序号}_{x}_{y}.png
 * 例：09-0_420_250-1_400_800.png
 *
 * 套装：本地根目录下数字子文件夹 01/02/03…，按人数循环分配。
 */
import {PIYING_KEYPOINT_MAP} from './params';

const FILENAME_RE =
    /^(\d+)-(\d+)_(\d+)_(\d+)-(\d+)_(\d+)_(\d+)\./;

/**
 * 解析皮影贴图文件名。
 * 返回 { order, anchors: [{ imageIndex, x, y }, ...] }
 */
export function parsePiyingFilename(filename) {
  const match = filename.match(FILENAME_RE);
  if (match == null) {
    return null;
  }
  return {
    order: parseInt(match[1], 10),
    anchors: [
      {
        imageIndex: parseInt(match[2], 10),
        x: parseInt(match[3], 10),
        y: parseInt(match[4], 10),
      },
      {
        imageIndex: parseInt(match[5], 10),
        x: parseInt(match[6], 10),
        y: parseInt(match[7], 10),
      },
    ],
  };
}

/** 图内骨骼序号 → MoveNet/虚拟关键点序号 */
export function imageIndexToKeypointIndex(imageIndex) {
  return PIYING_KEYPOINT_MAP[imageIndex];
}

/**
 * 根据图片内两个锚点与画布上两个对应点，计算 2D 仿射变换矩阵。
 * 两点确定平移、旋转、缩放（无剪切）；返回 Canvas setTransform 参数。
 */
export function computeAffineFromTwoPoints(src0, src1, dst0, dst1) {
  const sx = src1.x - src0.x;
  const sy = src1.y - src0.y;
  const srcLen = Math.hypot(sx, sy);
  if (srcLen < 1e-6) {
    return null;
  }

  const dx = dst1.x - dst0.x;
  const dy = dst1.y - dst0.y;
  const dstLen = Math.hypot(dx, dy);
  if (dstLen < 1e-6) {
    return null;
  }

  const scale = dstLen / srcLen;
  const angle = Math.atan2(dy, dx) - Math.atan2(sy, sx);
  const cos = Math.cos(angle) * scale;
  const sin = Math.sin(angle) * scale;

  const a = cos;
  const b = sin;
  const c = -sin;
  const d = cos;
  const e = dst0.x - (a * src0.x + c * src0.y);
  const f = dst0.y - (b * src0.x + d * src0.y);

  return {a, b, c, d, e, f};
}

/**
 * 将仿射矩阵与水平镜像矩阵合并。
 * 镜像：x' = canvasWidth - x（与骨骼层 translate + scaleX(-1) 一致）。
 */
export function composeMirrorAffine(matrix, canvasWidth) {
  return {
    a: -matrix.a,
    b: matrix.b,
    c: -matrix.c,
    d: matrix.d,
    e: canvasWidth - matrix.e,
    f: matrix.f,
  };
}

function loadImageFromUrl(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`皮影贴图加载失败: ${src}`));
    img.src = src;
  });
}

export class PiyingManager {
  constructor() {
    /**
     * @type {Array<{id: string, items: Array, objectUrls: string[]}>}
     */
    this.packs = [];
    this.loaded = false;
    this.rootName = '';
  }

  /** 释放已加载套装的 Object URL */
  clear() {
    for (const pack of this.packs) {
      for (const url of pack.objectUrls) {
        URL.revokeObjectURL(url);
      }
    }
    this.packs = [];
    this.loaded = false;
    this.rootName = '';
  }

  /**
   * 从 File System Access 选出的套装列表加载。
   * @param {Array<{id: string, files: File[]}>} packFiles
   * @param {string=} rootName 显示用根目录名
   */
  async loadFromPacks(packFiles, rootName = '') {
    this.clear();

    const packs = [];
    for (const pack of packFiles) {
      const items = [];
      const objectUrls = [];
      for (const file of pack.files) {
        const meta = parsePiyingFilename(file.name);
        if (meta == null) {
          console.warn(`跳过无法解析的皮影文件名: ${file.name}`);
          continue;
        }
        const url = URL.createObjectURL(file);
        objectUrls.push(url);
        const image = await loadImageFromUrl(url);
        items.push({...meta, name: file.name, image});
      }
      items.sort((a, b) => a.order - b.order);
      if (items.length === 0) {
        for (const url of objectUrls) {
          URL.revokeObjectURL(url);
        }
        console.warn(`套装 ${pack.id} 无有效贴图，已跳过`);
        continue;
      }
      packs.push({id: pack.id, items, objectUrls});
    }

    if (packs.length === 0) {
      throw new Error('皮影文件夹中没有可解析的贴图文件');
    }

    this.packs = packs;
    this.loaded = true;
    this.rootName = rootName;
  }

  isReady() {
    return this.loaded && this.packs.length > 0;
  }

  getPackCount() {
    return this.packs.length;
  }

  getPackIds() {
    return this.packs.map((p) => p.id);
  }

  /**
   * 按帧内人物序号循环取套装贴图。
   * 例：3 套、4 人 → 01,02,03,01
   */
  getItemsForPerson(personIndex) {
    if (!this.isReady()) {
      return [];
    }
    const pack = this.packs[personIndex % this.packs.length];
    return pack.items;
  }
}
